import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import useChartColors from "Components/Common/useChartColors";
import useQueryState from "Components/Hooks/UseQueryState";
import { getPaymentsRegionCategoryDistribution } from "common/services/payments.service";
import { getYearDateRange } from "common/lib/dateUtils";
import { useProfile } from "Components/Hooks/UserHooks";

const convertToSeries = (response: any[]) => {
  // Step 1: Extract all unique categories
  const categories = Array.from(
    new Set(
      response.flatMap((item) =>
        item.payments.map((payment: any) => payment.category)
      )
    )
  );

  // Step 2: Map each category to its totalCount values for each federalMemberState
  const countSeries = categories.map((category) => {
    return {
      name: category,
      data: response.map((item) => {
        const payment = item.payments.find(
          (payment: any) => payment.category === category
        );
        return payment ? payment.totalCount : 0; // Default to 0 if no payment for the category
      }),
    };
  });
  const amountSeries = categories.map((category) => {
    return {
      name: category,
      data: response.map((item) => {
        const payment = item.payments.find(
          (payment: any) => payment.category === category
        );
        return payment ? payment.totalAmount : 0; // Default to 0 if no payment for the category
      }),
    };
  });

  return { countSeries, amountSeries };
};

const ContractDistributionChart = ({
  series,
  categories,
}: {
  series: any[];
  categories: string[];
}) => {
  const dataColors = useChartColors("portfolio_donut_charts");

  const options: any = {
    chart: {
      stacked: !0,
      stackType: "100%",
      toolbar: {
        show: !1,
      },
    },
    plotOptions: {
      bar: {
        horizontal: !0,
      },
    },
    stroke: {
      width: 1,
      colors: ["#fff"],
    },
    title: {
      text: "",
      style: {
        fontWeight: 600,
      },
    },
    xaxis: {
      categories,
    },
    tooltip: {
      y: {
        formatter: function (val: any) {
          return val.toLocaleString() + " Contracts";
        },
      },
    },
    fill: {
      opacity: 1,
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
      offsetX: 40,
    },
    colors: dataColors,
  };

  return (
    <ReactApexChart
      dir="ltr"
      className="apex-charts"
      series={series}
      options={options}
      type="bar"
      height={350}
    />
  );
};

const PaymentCategoryAndRegion = () => {
  const { userProfile } = useProfile();
  const [payments, setPayments] = useState([]);
  const [, setLoading] = useState(false);

  const [selectedYear] = useQueryState(
    "year",
    new Date().getFullYear().toString()
  );
  const { firstDay, lastDay } = getYearDateRange(Number(selectedYear));

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response: any = await getPaymentsRegionCategoryDistribution({
          startDate: firstDay ? firstDay : undefined,
          endDate: lastDay ? lastDay : undefined,
          agencyId: userProfile?.agency?.id,
        });

        setPayments(response);
      } catch (error) {
        console.warn("Error fetching total  support", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [firstDay, lastDay, userProfile?.agency?.id]);

  const { countSeries } = React.useMemo(
    () => convertToSeries(payments),
    [payments]
  );
  const categories = useMemo(() => {
    return payments.map((payment: any) => payment.federalMemberState);
  }, [payments]);

  return (
    <Card>
      <CardHeader>
        <h4 className="card-title mb-0">
          {" "}
          Distribution of Individuals by Payment Category and Region
        </h4>
      </CardHeader>
      <CardBody>
        <ContractDistributionChart
          series={countSeries}
          categories={categories}
        />
      </CardBody>
    </Card>
  );
};

export default PaymentCategoryAndRegion;
